import React, { useEffect, useMemo, useState } from 'react';
import { DIRECTION_CONFIG, LM_TOKENS, SENTIMENT_CONFIG } from '../../live-monitor/config';
import type { LiveCall } from '../../live-monitor/types';
import { fmtDur, makeWaveBars, useTickingDuration } from '../../live-monitor/utils';
import { Icon } from '../Icon/Icon';

function MonitorWave({ active, color }: { active: boolean; color: string }) {
  const [tick, setTick] = useState(0);
  const bars = useMemo(() => makeWaveBars('static', 22), []);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((t) => t + 1), 220);
    return () => clearInterval(id);
  }, [active]);

  return (
    <div className="flex h-[22px] flex-1 items-center gap-0.5">
      {bars.map((h, i) => {
        const wobble = active ? 0.65 + 0.35 * Math.sin(tick * 0.7 + i * 0.5) : 0.55;
        const finalH = active ? h * wobble : h * 0.4;
        return (
          <div
            key={i}
            className="min-h-[2px] flex-1 rounded-[1.5px] transition-[height,opacity] duration-200"
            style={{
              height: `${Math.round(finalH * 100)}%`,
              background: color,
              opacity: active ? 1 : 0.55,
            }}
          />
        );
      })}
    </div>
  );
}

export interface LiveMonitorCallTileProps {
  call: LiveCall;
  isSelected: boolean;
  onClick: () => void;
}

export function LiveMonitorCallTile({ call, isSelected, onClick }: LiveMonitorCallTileProps) {
  const liveDur = useTickingDuration(call.duration);
  const dirCfg = DIRECTION_CONFIG[call.direction];
  const sentCfg = SENTIMENT_CONFIG[call.sentiment];

  const railColor = call.urgent ? LM_TOKENS.negative : call.onHold ? LM_TOKENS.warning : LM_TOKENS.green;
  const speakerColor = call.speaker === 'elva' ? LM_TOKENS.green : LM_TOKENS.brassDeep;
  const speakerBg = call.speaker === 'elva' ? LM_TOKENS.greenWash : LM_TOKENS.brassTint;
  const speakerBorder =
    call.speaker === 'elva' ? LM_TOKENS.greenSoft : 'rgba(168, 132, 92, 0.18)';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="flex min-w-0 cursor-pointer flex-col gap-3 rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] p-[18px] transition-[transform,box-shadow,border-color] duration-[220ms]"
      style={{
        borderLeft: `3px solid ${railColor}`,
        boxShadow: isSelected
          ? `0 0 0 2px ${LM_TOKENS.greenSoft}, ${LM_TOKENS.shadowMd}`
          : LM_TOKENS.shadowXs,
        transform: isSelected ? 'translateY(-1px)' : 'none',
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="lm-mono rounded px-[7px] py-px text-[10px] font-semibold"
          style={{
            background: 'var(--cream-0)',
            color: 'var(--ink-2)',
            border: '1px solid var(--cream-2)',
          }}
        >
          {call.num}
        </span>
        <span
          className="lm-mono rounded px-2 py-px text-[10px] font-bold uppercase tracking-[0.08em]"
          style={{
            background: `${dirCfg.color}1A`,
            color: dirCfg.color,
          }}
        >
          {dirCfg.short}
        </span>
        {call.urgent && (
          <span
            className="inline-flex items-center gap-[5px] rounded px-[7px] py-px text-[11px] font-semibold tracking-[-0.005em]"
            style={{ background: LM_TOKENS.negativeSoft, color: LM_TOKENS.negative }}
          >
            <Icon kind="alert" size={10} stroke={2} />
            Frustrated
          </span>
        )}
        {call.onHold && (
          <span
            className="inline-flex items-center gap-[5px] rounded px-[7px] py-px text-[11px] font-semibold tracking-[-0.005em]"
            style={{ background: LM_TOKENS.warningSoft, color: LM_TOKENS.warning }}
          >
            On hold
          </span>
        )}
        <span className="flex-1" />
        <span
          className="lm-mono text-[13px] font-bold"
          style={{ color: call.urgent ? LM_TOKENS.negative : 'var(--ink-0)' }}
        >
          {fmtDur(liveDur)}
        </span>
      </div>

      <div>
        <div className="mb-1 text-[17px] font-semibold leading-[1.2] tracking-[-0.02em] text-[var(--ink-0)]">
          {call.name}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-[var(--ink-2)]">
          <span className="lm-mono">{call.phone}</span>
          <span style={{ color: LM_TOKENS.ink5 }}>·</span>
          <span
            className="lm-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--ink-2)]"
          >
            {call.isReturning ? 'Returning' : 'New'}
          </span>
          <span style={{ color: LM_TOKENS.ink5 }}>·</span>
          <span className="font-medium">{call.goal}</span>
        </div>
      </div>

      <div
        className="flex items-center gap-2.5 rounded-lg px-3 py-2"
        style={{ background: speakerBg, border: `1px solid ${speakerBorder}` }}
      >
        <span
          className="inline-flex flex-none items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em]"
          style={{ color: speakerColor }}
        >
          <span
            className="inline-flex h-[17px] w-[17px] items-center justify-center rounded-full text-[9px] font-bold text-white"
            style={{ background: speakerColor }}
          >
            {call.speaker === 'elva' ? 'E' : 'P'}
          </span>
          {call.speaker === 'elva' ? 'Elva' : 'Caller'}
        </span>
        <MonitorWave active color={speakerColor} />
      </div>

      <p
        className="m-0 line-clamp-2 text-[13.5px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-1)]"
      >
        &ldquo;{call.utterance}&rdquo;
      </p>

      <div className="flex items-center justify-between border-t border-[var(--cream-2)] pt-3">
        <span className="flex min-w-0 flex-1 items-center gap-[7px] text-[12px] font-medium tracking-[-0.005em] text-[var(--ink-1)]">
          <span
            className={call.urgent ? 'lm-breathe' : ''}
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: call.urgent ? LM_TOKENS.negative : sentCfg.color,
              flex: 'none',
            }}
          />
          <span className="truncate">{call.status}</span>
        </span>
        <span className="lm-mono ml-2 flex-none text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink-2)]">
          NOW
        </span>
      </div>
    </div>
  );
}
